import { ObservabilityLogLevel } from "@inditex/core-o11y-js";
import { sendLog } from "src/utils/observability/observabilityUtils";
import { getOvixiaTokenFromCookie } from "src/utils/utils";
import { getUserFromCookies } from "src/utils/TokenTools";

const WEBVIEW_RELOAD_LABEL = "Webview Reload";

const getNavigationType = (): string => {
  try {
    const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (entries.length > 0) return entries[0].type;
  } catch { /* ignore */ }
  return 'unknown';
};

const getTimeSinceLastHidden = (): string => {
  try {
    const lastHidden = sessionStorage.getItem('_webview_last_hidden_ts');
    if (lastHidden) {
      const gap = Date.now() - Number(lastHidden);
      return String(gap);
    }
  } catch { /* ignore */ }
  return 'unknown';
};

const getPlatform = (): string => {
  try {
    const ua = window.navigator.userAgent;
    if (ua.includes('ZaraApp') && ua.includes('Android')) return 'android';
    if (ua.includes('ZaraApp') && ua.includes('iOS')) return 'ios';
  } catch { /* ignore */ }
  return 'web';
};

export const logWebviewReloadDetected = (imageCount: number, hasDescription: boolean): void => {
  const navigationType = getNavigationType();
  const backgroundGapMs = getTimeSinceLastHidden();
  const platform = getPlatform();
  const hasResellToken = Boolean(getOvixiaTokenFromCookie());
  const hasOAuthToken = Boolean(getUserFromCookies()?.accessToken);
  sendLog({
    message: `Webview reload detected during form editing. Images: ${imageCount}, hasDescription: ${hasDescription}, navigationType: ${navigationType}, backgroundGapMs: ${backgroundGapMs}, platform: ${platform}, hasResellToken: ${hasResellToken}, hasOAuthToken: ${hasOAuthToken}`,
    level: ObservabilityLogLevel.WARN,
    label: WEBVIEW_RELOAD_LABEL,
    tags: {
      image_count: String(imageCount),
      has_description: String(hasDescription),
      navigation_type: navigationType,
      background_gap_ms: backgroundGapMs,
      platform,
      has_resell_token: String(hasResellToken),
      has_oauth_token: String(hasOAuthToken),
    },
  });
};

export const logVisibilityChange = (state: string, pageUrl: string): void => {
  if (state === 'hidden') {
    try {
      sessionStorage.setItem('_webview_last_hidden_ts', String(Date.now()));
    } catch { /* ignore */ }
  }
  sendLog({
    message: `Page visibility changed to: ${state}`,
    level: ObservabilityLogLevel.INFO,
    label: WEBVIEW_RELOAD_LABEL,
    tags: {
      visibility_state: state,
      page_url: pageUrl,
    },
  });
};

export const logStorageQuotaExceeded = (error: string): void => {
  sendLog({
    message: `SessionStorage quota exceeded: ${error}`,
    level: ObservabilityLogLevel.ERROR,
    label: WEBVIEW_RELOAD_LABEL,
    tags: { error },
  });
};

export const logStorageError = (error: string): void => {
  sendLog({
    message: `SessionStorage write failed: ${error}`,
    level: ObservabilityLogLevel.ERROR,
    label: WEBVIEW_RELOAD_LABEL,
    tags: { error },
  });
};

export const logImageRehydrationFailed = (fileName: string, reason: string): void => {
  sendLog({
    message: `Image rehydration failed: ${fileName} - ${reason}`,
    level: ObservabilityLogLevel.ERROR,
    label: WEBVIEW_RELOAD_LABEL,
    tags: {
      file_name: fileName,
      reason,
    },
  });
};

export const logIndexedDBSaveFailed = (key: string, error: string): void => {
  sendLog({
    message: `IndexedDB save failed for blob ${key}: ${error}`,
    level: ObservabilityLogLevel.ERROR,
    label: WEBVIEW_RELOAD_LABEL,
    tags: {
      blob_key: key,
      error,
      operation: 'save',
    },
  });
};

export const logIndexedDBGetFailed = (key: string, error: string): void => {
  sendLog({
    message: `IndexedDB get failed for blob ${key}: ${error}`,
    level: ObservabilityLogLevel.ERROR,
    label: WEBVIEW_RELOAD_LABEL,
    tags: {
      blob_key: key,
      error,
      operation: 'get',
    },
  });
};

export const logIndexedDBBlobNotFound = (key: string): void => {
  sendLog({
    message: `Blob not found in IndexedDB: key=${key}`,
    level: ObservabilityLogLevel.WARN,
    label: WEBVIEW_RELOAD_LABEL,
    tags: {
      blob_key: key,
      operation: 'get',
    },
  });
};

export const logBlobSaveFallback = (fileName: string, index: number, total: number, error: string): void => {
  sendLog({
    message: `Failed to save blob for image ${fileName} [${index}/${total}], using file reference fallback`,
    level: ObservabilityLogLevel.WARN,
    label: WEBVIEW_RELOAD_LABEL,
    tags: {
      file_name: fileName,
      index: String(index),
      total: String(total),
      error,
    },
  });
};

export const logImageMissingData = (fileName: string): void => {
  sendLog({
    message: `Image missing blobKey and file/url: ${fileName}`,
    level: ObservabilityLogLevel.ERROR,
    label: WEBVIEW_RELOAD_LABEL,
    tags: {
      file_name: fileName,
    },
  });
};
